/**
 * <KpiCard label="Revenue" value={shortMoney(s.revenue)} current={s.revenue} previous={s.previous_revenue}
 *          compareLabel="vs previous 30 days" sparkline={trend} sparkKey="revenue" onClick={...} active />
 *
 * One headline number with its change in words:
 *   "up ₹28 K (+6.2%) vs previous 30 days" / "down 12 (−3.1%) vs same period last year"
 *   "No sales in the <comparison period>" when previous is 0 ("No returns in ..." with noun="returns"),
 *   "No comparison data" when a value is missing.
 *   Omit `previous` entirely (undefined) to hide the change line.
 * Props: label, value (formatted node), current, previous, kind 'money'|'number'|'percent'|'days',
 *   invert (lower is better: down shown green), compareLabel, noun (what was counted: 'sales' default), sparkline (array) + sparkKey
 *   (+ sparkCompareKey, sparkColor), onClick + active (renders a pressed-state button), footnote,
 *   hint (title on the label), loading (dims; skeleton only while value is null), testId.
 * Also exports changeSentence(current, previous, { kind, compareLabel, noun }) -> { text, direction }.
 */
import React from 'react';
import { Skeleton } from '../ui/skeleton';
import { Sparkline } from './Sparkline';
import { changeParts, number, shortMoney, signedPct } from './format';

const deltaWords = (delta, kind) => {
  const abs = Math.abs(delta);
  if (kind === 'percent') return `${abs.toFixed(1)} pts`;
  if (kind === 'days') return `${number(abs)} days`;
  if (kind === 'number') return number(abs);
  return shortMoney(abs);
};

export const changeSentence = (current, previous, { kind = 'money', compareLabel = 'vs previous period', noun = 'sales' } = {}) => {
  const parts = changeParts(current, previous);
  // Nothing to compare with: say so plainly rather than implying the item is new.
  if (parts.isNew) return { text: `No ${noun} ${compareLabel.replace(/^vs\s+/, 'in the ')}`, direction: 'new' };
  if (parts.direction === 'none') return { text: 'No comparison data', direction: 'none' };
  if (parts.direction === 'flat') return { text: `No change ${compareLabel}`, direction: 'flat' };
  const pct = kind !== 'percent' && parts.pct != null ? ` (${signedPct(parts.pct)})` : '';
  return { text: `${parts.direction} ${deltaWords(parts.delta, kind)}${pct} ${compareLabel}`, direction: parts.direction };
};

export const KpiCard = ({ label, value, current, previous, kind = 'money', invert = false, compareLabel, noun, sparkline, sparkKey = 'value', sparkCompareKey, sparkColor, onClick, active = false, footnote, hint, loading = false, className = '', testId }) => {
  const showChange = previous !== undefined;
  const change = showChange ? changeSentence(current, previous, { kind, compareLabel, noun }) : null;
  const good = change && (change.direction === 'up' ? !invert : change.direction === 'down' ? invert : null);
  const changeTone = !change ? '' : change.direction === 'new' ? 'text-sky-700'
    : good === true ? 'text-green-700' : good === false ? 'text-red-700' : 'text-gray-500';
  const content = (
    <>
      <span className="block text-xs font-medium uppercase tracking-wide text-gray-500" title={hint}>{label}</span>
      <span className={`mt-1 block min-h-[2rem] text-2xl font-semibold tabular-nums text-gray-900 ${loading && value != null ? 'opacity-60' : ''}`}>
        {loading && value == null ? <Skeleton className="h-7 w-24" /> : (value ?? '—')}
      </span>
      {showChange && (
        <span className={`mt-0.5 block text-xs ${changeTone} ${loading ? 'opacity-50' : ''}`} data-testid={testId ? `${testId}-change` : undefined}>
          {loading && current == null ? <Skeleton className="h-3 w-40" /> : change.text}
        </span>
      )}
      {sparkline && sparkline.length > 1 && (
        <Sparkline data={sparkline} dataKey={sparkKey} compareKey={sparkCompareKey} color={sparkColor} height={28} className={`mt-2 ${loading ? 'opacity-50' : ''}`} />
      )}
      {footnote && <span className="mt-2 block text-[11px] leading-snug text-gray-500">{footnote}</span>}
    </>
  );
  const shell = `min-w-0 rounded-xl border bg-white p-4 text-left shadow-sm ${active ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'} ${className}`;
  if (!onClick) return <div className={shell} data-testid={testId}>{content}</div>;
  return (
    <button type="button" onClick={onClick} aria-pressed={active}
      className={`${shell} w-full transition hover:border-blue-300 hover:shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500`} data-testid={testId}>
      {content}
    </button>
  );
};

export default KpiCard;
